import { defineComponent, ref, computed, onMounted, watch, type ComponentPublicInstance } from 'vue'
import { RouterLink } from 'vue-router'
import { useSmartBack } from '@/composables/use-smart-back'
import {
  NavBar as VanNavBar,
  Icon as VanIcon,
  Sticky as VanSticky,
  Search as VanSearch,
  Popup as VanPopup,
  Picker as VanPicker,
  Empty as VanEmpty,
} from 'vant'
import { useRegionStore } from '@/stores/region-store'
import { useArticleStore } from '@/stores/article-store'
import { useSeo, useSiteBrand } from '@/composables/use-seo'
import { getApiV1ArticleTypeList } from '@/api/sdk.gen'
import type { ArticleType } from '@/api/types.gen'
import LotterySwitcher from '@/components/lottery-switcher'

const CURRENT_YEAR = new Date().getFullYear()

export default defineComponent({
  name: 'ArticleTypeListPage',
  setup() {
  const goBack = useSmartBack()
    const regionStore = useRegionStore()
    const articleStore = useArticleStore()

    const selectedYear = ref(CURRENT_YEAR)
    const keyword = ref('')
    const confirmedKeyword = ref('')
    const types = ref<ArticleType[]>([])
    const showYear = ref(false)

    // SEO：资料大全总入口（站点名取后台配置）
    const brand = useSiteBrand()
    useSeo({
      title: () => `六合资料大全_玄机资料图纸文章汇总 - ${brand.value}`,
      description: () =>
        `${brand.value}六合资料大全，汇集各类玄机资料、图纸与文章，按年份与彩种分类，持续更新。`,
      keywords: () => `六合资料大全,玄机资料,图纸,资料文章,${brand.value}`,
    })

    const lotteryType = computed<1 | 2>(() => (regionStore.region === 'hk' ? 1 : 2))

    const yearColumns = computed(() =>
      articleStore.yearList.map((y) => ({ text: String(y), value: y })),
    )

    const filteredTypes = computed(() => {
      const kw = confirmedKeyword.value
      if (!kw) return types.value
      return types.value.filter((t) => t.name?.includes(kw))
    })

    async function fetchTypes() {
      const res = await getApiV1ArticleTypeList({
        query: { year: selectedYear.value, lotteryType: lotteryType.value },
      })
      const data = res.data?.data
      types.value = data?.list ?? []
      if (data?.yearList && data.yearList.length > 0) {
        articleStore.yearList = data.yearList
      }
    }

    onMounted(fetchTypes)
    watch(() => regionStore.region, fetchTypes)

    // vant Field 的 clear 仅监听 touchstart 且 clearTrigger 默认 focus，
    // 桌面端鼠标 click 时 × 已随失焦消失，需在 mousedown 阶段拦截；× 在 VanSearch 内部，委托到它的根节点
    const searchRef = ref<ComponentPublicInstance | null>(null)
    onMounted(() => {
      searchRef.value?.$el.addEventListener('mousedown', (e: MouseEvent) => {
        const t = e.target as HTMLElement
        if (t.closest('.van-field__clear')) {
          e.preventDefault()
          keyword.value = ''
          confirmedKeyword.value = ''
        }
      })
    })

    function onSearch() {
      confirmedKeyword.value = keyword.value.trim()
    }

    function onConfirmYear({ selectedValues }: { selectedValues: number[] }) {
      showYear.value = false
      const y = selectedValues[0]
      if (y && y !== selectedYear.value) {
        selectedYear.value = y
        fetchTypes()
      }
    }

    return () => (
      <div class="corpus-wrap">
        <VanSticky>
          <VanNavBar title="资料大全" onClickLeft={() => goBack()}>
            {{
              left: () => <VanIcon name="arrow-left" color="#333" size="16" />,
              right: () => (
                <div class="flex items-center gap-1.5">
                  <VanIcon
                    name="notes-o"
                    color="#07c160"
                    size="16"
                    onClick={() => (showYear.value = true)}
                  />
                  <LotterySwitcher />
                </div>
              ),
            }}
          </VanNavBar>
        </VanSticky>

        <VanSearch
          ref={searchRef}
          v-model={keyword.value}
          placeholder="搜索相关标题文章"
          shape="round"
          showAction
          background="#f8f8f8"
          onSearch={onSearch}
          onClear={() => (confirmedKeyword.value = '')}
        >
          {{
            action: () => (
              <button type="button" class="btn-reset block" style="font-size:0.3rem;color:#0bbd63" onClick={onSearch}>
                搜索
              </button>
            ),
          }}
        </VanSearch>

        <div class="search-list clearfix">
          {filteredTypes.value.map((t) => (
            <RouterLink
              key={t.typeId}
              class="search-item btn-reset"
              to={`/articles/list?year=${selectedYear.value}&typeId=${t.typeId}&typeName=${encodeURIComponent(t.name ?? '')}&lotteryType=${lotteryType.value}`}
            >
              <div innerHTML={t.name ?? ''} />
            </RouterLink>
          ))}
          {filteredTypes.value.length === 0 && <VanEmpty description="暂无数据" />}
        </div>

        <VanPopup v-model:show={showYear.value} position="bottom">
          <VanPicker
            title="选择年份"
            columns={yearColumns.value}
            onConfirm={onConfirmYear}
            onCancel={() => (showYear.value = false)}
          />
        </VanPopup>
      </div>
    )
  },
})
